import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Button, Divider, Grid, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { isEqual } from 'lodash';

import * as moment from 'moment';

import { withTranslation } from 'react-i18next';
import EuroIcon from '@mui/icons-material/Euro';

import Price from './Price';
import CustomIcon from '../../utils/CustomIcon';
import { settings } from '../../sites';

const inlineStyles = theme => ({
  root: {
    background: theme.box.background,
    borderRadius: theme.box.borderRadius,
    padding: theme.spacing(3, 2),
    marginBottom: 24,
    textAlign: 'left',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      padding: '36px 20px',
    },
  },
  rowContainer: {
    display: 'flex',
    marginBottom: '2rem',
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  heading: {
    marginBottom: 0,
  },
  text: {
    marginTop: '4px',
  },
  icon: {
    margin: theme.spacing('-2px', 2, 0, 0),
  },
  description: {
    marginTop: 20,
  },
  bookingInfoTitle: {
    marginBottom: theme.spacing(4),
  },
  divider: {
    marginBottom: theme.spacing(3),
  },
  cancelButton: {
    marginTop: theme.spacing(2),
  },
});

/**
 * SelectedServiceBox
 * Use to show information of selected service
 * @param {Object} classes     Classes from JSS inlineStyles
 * @param {Object} content     Content object (headline, caption, price,
 * date, time, person, address, phone, email, location)
 */

const SelectedServiceBox = ({
  classes,
  content,
  bookingReference,
  showCancellation,
  appointmentID,
  cancelAppointment,
  view,
  i18n,
  t,
}) => {
  const { headline, price, person, address, description, timeSlot, storeName, opticianTitle } = content;

  const getLocale = () => i18n.language;

  const locale = getLocale() ? getLocale() : 'fi';

  const startTimeMoment = moment(timeSlot, 'YYYY-MM-DDTHH:mm').locale(locale);

  const dateFormatted = startTimeMoment.format('D.M.YYYY');
  const dayOfTheWeek = startTimeMoment.format('dddd');

  return (
    <div className={classes.root}>
      {isEqual(view, 'ThankYou') && (
        <Grid container direction="row" spacing={0}>
          <Grid item xs={12} align="left">
            <Typography className={classes.bookingInfoTitle} variant="h3" component="h2">
              {t('views.thankYou.bookingInfoTitle')}:
            </Typography>
          </Grid>
        </Grid>
      )}

      <div className={classes.rowContainer}>
        {description && (
          <div>
            <Typography variant="h3" component="h2" className={classes.heading}>
              {headline}
            </Typography>
            <Typography className={classes.description} variant="body1">
              {description}
            </Typography>
          </div>
        )}
        {timeSlot && (
          <>
            <CustomIcon className={classes.icon} icon="calendar" />

            <div>
              <Typography variant="h4" component="h2" className={classes.heading}>
                {headline}
              </Typography>

              <Typography className={classes.text} variant="body1">
                {t('dateTime.onBefore')} {dayOfTheWeek} <strong>{dateFormatted}</strong> {t('dateTime.clock')}{' '}
                <strong>{startTimeMoment.format('H:mm')}</strong>
              </Typography>
            </div>
          </>
        )}
      </div>

      {storeName && (
        <div className={classes.rowContainer}>
          <CustomIcon className={classes.icon} icon="location" />
          <div>
            <Typography variant="h4" component="h3" className={classes.heading}>
              {storeName}
            </Typography>
            {address && (
              <Typography className={classes.text} variant="body1">
                {address}
              </Typography>
            )}
          </div>
        </div>
      )}

      {get(settings, 'showOpticians') && person && (
        <div className={classes.rowContainer}>
          <CustomIcon className={classes.icon} icon="user" />
          <div>
            <Typography className={classes.heading} variant="h4" component="h3">
              {person}
            </Typography>
            <Typography className={classes.text} variant="body1">
              {opticianTitle}
            </Typography>
          </div>
        </div>
      )}

      {price && price.primaryPrice && (
        <div className={classes.rowContainer}>
          {!description && <EuroIcon className={classes.icon} />}
          <Typography variant="h4" component="h3">
            <Price prices={price} template="compact" />
          </Typography>
        </div>
      )}

      {get(settings, 'showMyAppointments') && bookingReference && (
        <div className={classes.rowContainer}>
          <CustomIcon className={classes.icon} icon="digits" />
          <div>
            <Typography variant="h4" component="h3" className={classes.heading}>
              {t('views.thankYou.bookingReferenceId')}: {bookingReference}
            </Typography>
          </div>
        </div>
      )}
      {showCancellation && (
        <div>
          <Divider className={classes.divider} />
          <Typography variant="body2">{t('views.appointmentDetails.instructions')}</Typography>
          <Button
            className={classes.cancelButton}
            disableRipple
            variant="outlined"
            onClick={() => cancelAppointment(appointmentID)}>
            {t('views.appointmentLogin.cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};

SelectedServiceBox.propTypes = {
  appointmentID: PropTypes.string,
  cancelAppointment: PropTypes.func,
  bookingReference: PropTypes.string,
  classes: PropTypes.object,
  content: PropTypes.object,
  i18n: PropTypes.any,
  showCancellation: PropTypes.bool,
  t: PropTypes.func,
  view: PropTypes.string,
};

SelectedServiceBox.defaultProps = {
  appointmentID: '',
  bookingReference: '',
  cancelAppointment: () => {},
  classes: {},
  content: {},
  i18n: null,
  showCancellation: false,
  t: null,
  view: '',
};

export default withStyles(inlineStyles)(withTranslation('translations')(SelectedServiceBox));
